import { LicenseLine } from "@/components/LicenseLine";
import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AffiliateCTA } from "@/components/AffiliateCTA";
import { BrandLogo } from "@/components/BrandLogo";
import { RatingStars } from "@/components/RatingStars";
import { RiskNotice } from "@/components/RiskNotice";
import { FAQ, buildFAQSchema } from "@/components/FAQ";
import { findCasino, listCasinos } from "@/data/casinos";
import { CasinoCard } from "@/components/CasinoCard";
import { InternalLinks } from "@/components/InternalLinks";
import { findPayment } from "@/data/payments";
import { findProvider } from "@/data/providers";
import { NotFoundPage } from "./NotFoundPage";
import { paymentPath, providerPath, casinoReviewPath } from "@/lib/format";

export function CasinoReviewPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const casino = findCasino(slug);
  if (!casino) return <NotFoundPage />;

  const intro = isSv ? casino.intro_sv : casino.intro_en;
  const body = isSv ? casino.body_sv : casino.body_en;
  const pros = isSv ? casino.pros_sv : casino.pros_en;
  const cons = isSv ? casino.cons_sv : casino.cons_en;
  const bonusSummary = isSv ? casino.bonus_summary_sv : casino.bonus_summary_en;

  const faq = [
        {
          question_sv: `Har ${casino.name} svensk licens?`,
          question_en: `Does ${casino.name} have a Swedish license?`,
          answer_sv: casino.swedish_license
            ? `Ja, ${casino.name} har svensk spellicens utfärdad av Spelinspektionen.`
            : `Nej, ${casino.name} har inte svensk spellicens. Kontrollera alltid licens innan du registrerar konto.`,
          answer_en: casino.swedish_license
            ? `Yes, ${casino.name} holds a Swedish license from Spelinspektionen.`
            : `No, ${casino.name} does not hold a Swedish license. Always verify the license before registering.`,
        },
        {
          question_sv: `Är ${casino.name} anslutet till Spelpaus?`,
          question_en: `Is ${casino.name} connected to Spelpaus?`,
          answer_sv: casino.spelpaus_connected
            ? `Ja, ${casino.name} är anslutet till spelpaus.se.`
            : `Nej, ${casino.name} är inte anslutet till spelpaus.se.`,
          answer_en: casino.spelpaus_connected
            ? `Yes, ${casino.name} is connected to spelpaus.se.`
            : `No, ${casino.name} is not connected to spelpaus.se.`,
        },
        {
          question_sv: `Vilka betalningsmetoder finns hos ${casino.name}?`,
          question_en: `What payment methods does ${casino.name} support?`,
          answer_sv: `Bland annat ${casino.payment_methods.join(", ")}.`,
          answer_en: `Including ${casino.payment_methods.join(", ")}.`,
        },
      ];

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "Organization",
      name: casino.name,
      url: casino.website_url,
    },
    author: { "@type": "Organization", name: "CasinoMenu.se" },
    reviewRating: {
      "@type": "Rating",
      ratingValue: casino.overall_rating,
      bestRating: 5,
      worstRating: 1,
    },
  };

  return (
    <div className="container-page py-8">
      <SEO
        title={
          isSv
            ? `${casino.name} recension – betyg ${casino.overall_rating.toFixed(1)}/5`
            : `${casino.name} review – rating ${casino.overall_rating.toFixed(1)}/5`
        }
        description={`${casino.name} — ${intro ?? (isSv ? "casino-recension" : "casino review")}`}
        type="article"
        path={casinoReviewPath(prefix, lang, casino.slug)}
        jsonLd={[reviewSchema, buildFAQSchema(faq, lang)]}
      />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          {
            label: isSv ? "Recensioner" : "Reviews",
            href: `${prefix}/${isSv ? "recensioner" : "reviews"}`,
          },
          { label: casino.name },
        ]}
      />

      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_320px]">
        <article className="space-y-8">
          <header className="card p-6">
            <div className="flex flex-wrap items-center gap-4">
              <BrandLogo
                slug={casino.slug}
                name={casino.name}
                src={casino.logo_url}
                size={80}
                rounded="2xl"
              />
              <div>
                <h1 className="text-3xl font-extrabold text-ink-900">{casino.name}</h1>
                <div className="mt-1 flex items-center gap-3">
                  <RatingStars value={casino.overall_rating} />
                  {casino.priority_brand && (
                    <span className="pill-gold">★ {t("label.priorityBrand")}</span>
                  )}
                </div>
                {casino.website_url && (
                  <p className="mt-1 text-xs text-ink-500">
                    {casino.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 text-sm">
              {(
                [
                  ["safety", "rating.safety"],
                  ["bonus", "rating.bonus"],
                  ["payments", "rating.payments"],
                  ["games", "rating.games"],
                  ["mobile", "rating.mobile"],
                  ["support", "rating.support"],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="rounded-lg bg-ink-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-ink-500">
                    {t(label as never)}
                  </p>
                  <p className="text-lg font-bold text-ink-900">
                    {casino.ratings[key].toFixed(1)}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {casino.swedish_license ? (
                <span className="pill-success">{t("label.swedishLicense")}</span>
              ) : (
                <span className="pill-warn">{t("label.noSwedishLicense")}</span>
              )}
              {casino.spelpaus_connected ? (
                <span className="pill-success">{t("label.spelpausConnected")}</span>
              ) : (
                <span className="pill-warn">{t("label.notSpelpaus")}</span>
              )}
              {casino.swish_supported && <span className="pill-brand">{t("label.swish")}</span>}
              {casino.bankid_supported && <span className="pill-brand">{t("label.bankid")}</span>}
              {casino.trustly_supported && <span className="pill-brand">{t("label.trustly")}</span>}
              {casino.zimpler_supported && <span className="pill-brand">{t("label.zimpler")}</span>}
              {casino.brite_supported && <span className="pill-brand">{t("label.brite")}</span>}
              {casino.crypto_supported && <span className="pill-brand">{t("label.crypto")}</span>}
            </div>
          </header>

          {!casino.swedish_license && <RiskNotice variant="unlicensed" />}
          {!casino.spelpaus_connected && casino.accepts_sweden && (
            <RiskNotice variant="spelpaus" />
          )}

          <section>
            <h2 className="text-2xl font-bold">{t("review.aboutCasino")}</h2>
            {intro && (
              <p className="mt-3 text-lg leading-relaxed text-ink-800">{intro}</p>
            )}
            {body && (
              <p className="mt-3 text-base leading-relaxed text-ink-700">{body}</p>
            )}
          </section>

          {casino.long_sections && casino.long_sections.length > 0 && (
            <div className="space-y-8">
              {casino.long_sections.map((s, i) => {
                const paragraphs = isSv ? s.paragraphs_sv : s.paragraphs_en;
                return (
                  <section key={i}>
                    <h2 className="text-2xl font-bold">
                      {isSv ? s.heading_sv : s.heading_en}
                    </h2>
                    <div className="mt-3 space-y-3 text-base leading-relaxed text-ink-700">
                      {paragraphs.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          <section>
            <h2 className="text-2xl font-bold">{t("review.bonusInfo")}</h2>
            {bonusSummary && <p className="mt-2 text-ink-700">{bonusSummary}</p>}
            <p className="mt-2 text-xs text-ink-500">
              {isSv
                ? "Bonusvillkor kan ändras. Kontrollera alltid aktuella villkor på casinots webbplats."
                : "Bonus terms can change. Always verify current terms at the casino's website."}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold">{t("review.paymentMethods")}</h2>
            <ul className="mt-2 flex flex-wrap gap-2">
              {casino.payment_methods.map((slug) => {
                const pm = findPayment(slug);
                if (!pm) return null;
                return (
                  <li key={slug}>
                    <a
                      href={paymentPath(prefix, lang, isSv ? pm.slug_sv : pm.slug_en)}
                      className="pill-brand"
                    >
                      {pm.name}
                    </a>
                  </li>
                );
              })}
            </ul>
            {casino.withdrawal_speed && (
              <p className="mt-3 text-sm text-ink-700">
                <strong>{t("label.withdrawalSpeed")}:</strong> {casino.withdrawal_speed}
              </p>
            )}
          </section>

          <section>
            <h2 className="text-2xl font-bold">{t("review.gameProviders")}</h2>
            <ul className="mt-2 flex flex-wrap gap-2">
              {casino.game_providers.map((slug) => {
                const pr = findProvider(slug);
                if (!pr) return null;
                return (
                  <li key={slug}>
                    <a href={providerPath(prefix, lang, pr.slug)} className="pill-brand">
                      {pr.name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold">{t("review.prosAndCons")}</h2>
            <div className="mt-2 grid gap-4 md:grid-cols-2">
              <div className="card p-4">
                <h3 className="font-semibold text-emerald-700">{t("review.pros")}</h3>
                <ul className="mt-2 space-y-1 text-sm text-ink-700">
                  {pros.map((p) => (
                    <li key={p} className="flex gap-1">
                      <span className="text-emerald-600">✓</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card p-4">
                <h3 className="font-semibold text-rose-700">{t("review.cons")}</h3>
                <ul className="mt-2 space-y-1 text-sm text-ink-700">
                  {cons.map((p) => (
                    <li key={p} className="flex gap-1">
                      <span className="text-rose-600">✗</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {faq.length > 0 && <FAQ items={faq} />}

          <section>
            <h2 className="text-2xl font-bold">
              {isSv ? "Liknande casinon" : "Similar casinos"}
            </h2>
            <div className="mt-3 grid gap-4">
              {listCasinos({
                swedishLicense: casino.swedish_license,
              })
                .filter((c) => c.slug !== casino.slug)
                .slice(0, 3)
                .map((c, i) => (
                  <CasinoCard key={c.id} casino={c} rank={i + 1} source={`similar-${casino.slug}`} />
                ))}
            </div>
          </section>
        </article>

        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="card space-y-3 p-5">
            <div className="flex items-center gap-3">
              <BrandLogo
                slug={casino.slug}
                name={casino.name}
                src={casino.logo_url}
                size={48}
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink-900">
                  {casino.name}
                </p>
                <p className="text-xs text-ink-500">
                  {isSv ? "Besök casinot direkt" : "Visit the casino directly"}
                </p>
              </div>
            </div>
          <LicenseLine casino={casino} />
            <AffiliateCTA brandSlug={casino.slug} source="review" placement="sidebar" size="lg" />
            <p className="text-xs text-ink-500">{t("affiliate.disclosure")}</p>
            <hr className="border-ink-100" />
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-500">{t("rating.overall")}</dt>
                <dd className="font-semibold text-ink-900">
                  {casino.overall_rating.toFixed(1)}/5
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-500">{isSv ? "Licens" : "License"}</dt>
                <dd className="font-semibold text-ink-900">
                  {casino.license_authority ?? t("label.unknown")}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-500">{isSv ? "Operatör" : "Operator"}</dt>
                <dd className="font-semibold text-ink-900">
                  {casino.operator_name ?? t("label.unknown")}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-500">{t("label.lastUpdated")}</dt>
                <dd className="font-semibold text-ink-900">{isSv ? "22 oktober 2026" : "22 October 2026"}</dd>
              </div>
            </dl>
          </div>
          <div className="card p-5 text-sm text-ink-700">
            <strong className="block text-ink-900">
              {isSv ? "Ansvarsfullt spelande" : "Responsible gambling"}
            </strong>
            <p className="mt-1">{t("responsible.notice")}</p>
          </div>
        </aside>
      </div>

      <InternalLinks context="casino" exclude={[casino.slug]} />
    </div>
  );
}
